import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  SniperSession,
  SniperSessionsParams,
  getSniperSessions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface SniperSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const SniperSection: React.FC<SniperSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const params = useMemo<SniperSessionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
  }), [dateFrom, dateTo, symbol]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } = useFlowData<SniperSession, SniperSessionsParams>({
    queryKeyBase: ['admin', 'trade-flow', 'sniper'],
    fetchFn: (p) => getSniperSessions(p).then(r => r),
    params,
    refetchInterval: 10000,
  });

  return (
    <DataSection
      title="Smart Entry Sniper"
      tabNumber={10}
      description="Entry timing optimization that polls the market to find the best execution price window for each recommendation."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Rec ID</th>
            <th>Decision</th>
            <th>Best Score</th>
            <th>Polls</th>
            <th>Adverse Move</th>
            <th>Window</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.recommendation_id}>
              <td className={styles.monoSmall}>
                {d.evaluated_at ? new Date(d.evaluated_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td className={styles.monoSmall}>{d.recommendation_id.slice(0, 12)}...</td>
              <td>
                <span className={`${styles.sniperDecision} ${styles[d.decision === 'USE_SNIPER' ? 'sniperUse' : 'sniperBaseline']}`}>
                  {d.decision || '-'}
                </span>
              </td>
              <td className={styles.mono}>
                {d.best_score !== null ? d.best_score.toFixed(4) : '-'}
              </td>
              <td>{d.poll_count ?? '-'}</td>
              <td className={styles.mono}>
                {d.adverse_move_pct !== null ? `${d.adverse_move_pct.toFixed(2)}%` : '-'}
              </td>
              <td className={styles.mono}>
                {d.window_duration_seconds !== null
                  ? `${d.window_duration_seconds.toFixed(0)}s`
                  : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default SniperSection;
